import 'core-js/stable';
import 'regenerator-runtime/runtime';

import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import Amplify, { Auth, API } from 'aws-amplify';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloLink,
  Observable,
} from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import { BrowserRouter as Router } from 'react-router-dom';

import 'flag-icon-css/css/flag-icon.min.css';
import config from './config';
import { initTelemetry } from './telemetry/telemetry';

import { redirectLogin } from './utils';
import { LOCAL_AUTH_SHIM, installAuthShim, localUserResolve } from './utils/authShim';
import { handleSessionLost, isInvalidUserSession } from './utils/session';
import { App, NetworkError } from './pages';
import {
  ClientProvider,
  SnackbarProvider,
  ThemeProvider,
  PageProvider,
  AuthProvider,
} from './contexts';
import { LastLocationProvider } from './LastLocationProvider'; // Import the custom provider
import Bitrix24Form from './components/Bitrix24/Bitrix24Form';
import './localization/translations/i18n';
import './index.css';

global.fetch = require('node-fetch');

// OTel browser tracing (ERD-7). AFTER the fetch replacement above, so the instrumented
// window.fetch is the one Apollo resolves per request; BEFORE any client sends. The SDK arrives in
// its own async chunk, so this resolves a tick or two later; Apollo resolves the global fetch per
// request, not at link construction, so anything dispatched before then is simply untraced
// (fail open). A no-op — and absent from the bundle — unless both REACT_APP_OTEL_EXPORTER_OTLP_
// ENDPOINT and REACT_APP_OTEL_EXPORTER_OTLP_HEADERS are set at build time.
initTelemetry();

const ignoreErrors = [
  'No current user',
  'Refresh Token has expired',
];

Bugsnag.start({
  apiKey: '8e33173812161b8180ba2b14c6b7a3f4',
  releaseStage: config.app.stage,
  appVersion: config.app.version,
  enabledReleaseStages: ['prod', 'demo'],
  onError: (event) => {
    if (ignoreErrors.includes(event.originalError.message)) {
      window.location.href = `${config.app.secureUrl}${redirectLogin()}`;
      return false;
    }
    return true;
  },
  plugins: [new BugsnagPluginReact(React)],
});

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

Amplify.configure({
  Auth: {
    mandatorySignIn: true,
    region: config.app.region,
    userPoolId: config.cognito.userPoolId,
    identityPoolId: config.cognito.identityPoolId,
    userPoolWebClientId: config.cognito.appClientId,
    cookieStorage: {
      domain: config.cognito.cookieDomain,
      secure: config.cognito.cookieSecure,
    },
  },
  Storage: {
    // NOTE: Public bucket is not configured as Amplify only supports one Bucket at a time.
    // Still able to call public bucket when using Storage module.
    region: config.app.region,
    bucket: config.s3.bucket,
    identityPoolId: config.cognito.identityPoolId,
  },
  API: {
    endpoints: [
      // LMS-2843
      // {
      //   name: 'CHAT',
      //   endpoint: config.api.chatApiUrl,
      // },
      {
        name: 'SHORTURL',
        endpoint: config.api.shortUrlApiUrl,
      },
      {
        name: 'CREDITCHECK',
        endpoint: config.api.creditCheckApiUrl,
        region: config.app.region,
      },
      {
        name: 'COMPANY_BRANCH',
        endpoint: config.api.companyBranchApiUrl,
        region: config.app.region,
      },
      {
        name: 'LEAD',
        endpoint: config.api.leadApiUrl,
        region: config.app.region,
      },
      {
        name: 'WORKFLOW',
        endpoint: config.api.workflowApiUrl,
        region: config.app.region,
      },
      {
        name: 'USER',
        endpoint: config.api.userApiUrl,
        region: config.app.region,
      },
    ],
  },
});

// LOCAL-DEV ONLY: stub Cognito + the user API gateway before any client is built, so the authLink
// below picks up the forged local token. No-op unless REACT_APP_LOCAL_AUTH_SHIM=1.
installAuthShim(Auth, API);

// LOCAL-DEV ONLY: mhub-user-api has no local/ stack, so answer its operations from fixtures instead
// of letting clientUser reach real AWS.
const localUserLink = () => new ApolloLink((operation) => new Observable((observer) => {
  const name = operation.operationName || getOperationName(operation.query) || '';
  observer.next({ data: localUserResolve(name) });
  observer.complete();
}));

const generateLink = (endpoint) => {
  const httpLink = createHttpLink({ uri: endpoint });

  // A session taken over by another sign-in surfaces as `invalid user session` on every
  // subsequent query. Handled here, once, for every client. The alternative is what users
  // actually saw: a generic "please try again" snackbar, or a detail page rendered from
  // enhancedQuery's defaultData with nothing in it. Neither says the true thing, and no
  // amount of retrying fixes it. This is the same hand-off the authLink below already
  // performs when the token itself is missing.
  //
  // Skipped under the local-dev shim, where a seeded session row and a forged identity make
  // this condition an artefact of the offline stack rather than a real takeover.
  const sessionLink = onError(({ graphQLErrors }) => {
    if (LOCAL_AUTH_SHIM || !isInvalidUserSession(graphQLErrors)) return;
    handleSessionLost(config.app.secureUrl);
  });

  const authLink = setContext(async (_, { headers }) => {
    try {
      const session = await Auth.currentSession();
      const token = session.getIdToken().getJwtToken();
      return {
        headers: {
          ...headers,
          Authorization: token || '',
        },
      };
    } catch (err) {
      window.location.href = `${config.app.secureUrl}${redirectLogin()}`;
      return {
        headers: {
          ...headers,
        },
      };
    }
  });
  return sessionLink.concat(authLink).concat(httpLink);
};

// generateClient generate ApolloClient with link ( appsync and apollo links )
const generateClient = (link) => new ApolloClient({ link, cache: new InMemoryCache() });

export const clientAuth = generateClient(generateLink(config.appsync.authGraphqlEndpoint));
export const clientActivity = generateClient(generateLink(config.appsync.activityGraphqlEndpoint));
export const clientAudience = generateClient(generateLink(config.appsync.audienceGraphqlEndpoint));
export const clientLead = generateClient(generateLink(config.appsync.leadFieldsGraphqlEndpoint));
export const clientLeadIntegration = generateClient(
  generateLink(config.appsync.leadIntegrationGraphqlEndpoint),
);
export const clientCampaign = generateClient(generateLink(config.appsync.campaignGraphqlEndpoint));
export const clientCalls = generateClient(generateLink(config.appsync.callsGraphqlEndpoint));
export const clientContact = generateClient(generateLink(config.appsync.contactGraphqlEndpoint));
export const clientCustomer = generateClient(generateLink(config.appsync.customerGraphqlEndpoint));
export const clientForm = generateClient(generateLink(config.appsync.formGraphqlEndpoint));
export const clientOpportunity = generateClient(
  generateLink(config.appsync.opportunityGraphqlEndpoint),
);
export const clientSubmission = generateClient(
  generateLink(config.appsync.submissionGraphqlEndpoint),
);
export const clientSubscription = generateClient(
  generateLink(config.appsync.subscriptionGraphqlEndpoint),
);
export const clientUser = generateClient(
  LOCAL_AUTH_SHIM ? localUserLink() : generateLink(config.appsync.userGraphqlEndpoint),
);
export const clientBuyerAccess = generateClient(
  generateLink(config.appsync.buyerAccessGraphqlEndpoint),
);

class LocalizedUtils extends MomentUtils {
  getWeekdays() {
    return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => this.moment().weekday(dayOfWeek).format('dd')[0]);
  }
}

ReactDOM.render(
  <ClientProvider
    clients={{
      clientAuth,
      clientActivity,
      clientAudience,
      clientLead,
      clientLeadIntegration,
      clientCampaign,
      clientCalls,
      clientContact,
      clientCustomer,
      clientForm,
      clientOpportunity,
      clientSubmission,
      clientSubscription,
      clientUser,
      clientBuyerAccess,
    }}
  >
    <ErrorBoundary FallbackComponent={NetworkError}>
      <ThemeProvider>
        <SnackbarProvider>
          <AuthProvider>
            <PageProvider>
              <MuiPickersUtilsProvider utils={LocalizedUtils}>
                <Router>
                  <LastLocationProvider>
                    <Suspense fallback={<div>Loading...</div>}>
                      <Bitrix24Form
                        src="https://cdn.bitrix24.com/b20030593/crm/form/loader_117.js"
                        data="inline/117/h6zfew"
                        showAsPopup
                      />
                      <App />
                    </Suspense>
                  </LastLocationProvider>
                </Router>
              </MuiPickersUtilsProvider>
            </PageProvider>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </ClientProvider>,
  document.getElementById('root'),
);
